import type { CompanionState } from '@/lib/companion/types';
import type { Daypart } from './daypart';

export type Rng = () => number;

/** Of rest beats, the fraction that plays a stationary activity instead of
 *  plain idling. Movement stays governed by REST_CHANCE in dwell.ts — this
 *  only changes what a rest beat looks like, not how often one happens.
 *  If ambient feels too busy in practice, this is the single dial. */
export const AMBIENT_CHANCE = 0.5;

export const AMBIENT_WEIGHTS: Record<string, number> = {
  'look-around': 3,
  'play-with-orb': 2,
  boba: 2,
  stretch: 1,
  sit: 1,
  reading: 1,
};

/** The CompanionState each ambient row name drives. */
export const AMBIENT_STATE: Record<string, CompanionState> = {
  'look-around': 'lookingAround',
  'play-with-orb': 'playingWithOrb',
  boba: 'drinking',
  stretch: 'stretching',
  sit: 'sitting',
  reading: 'reading',
};

/** Per-daypart multipliers over AMBIENT_WEIGHTS. A missing entry means 1;
 *  a 0 removes the pose from the draw for that part of day. Base preference
 *  stays in AMBIENT_WEIGHTS — this only bends it. */
export const DAYPART_WEIGHTS: Record<Daypart, Record<string, number>> = {
  morning: { stretch: 3, 'look-around': 2, boba: 2, sit: 0.5 },
  day: { 'play-with-orb': 1.5, reading: 1.5 },
  evening: { reading: 2, boba: 1.5, 'play-with-orb': 0.5 },
  night: { sit: 3, stretch: 2, reading: 1.5, 'look-around': 0.5, 'play-with-orb': 0 },
};

export function pickAmbient(
  rng: Rng,
  available: readonly string[],
  daypart?: Daypart,
): string | null {
  if (rng() >= AMBIENT_CHANCE) return null;

  const bend = daypart ? DAYPART_WEIGHTS[daypart] : undefined;
  const weightOf = (name: string) => AMBIENT_WEIGHTS[name]! * (bend?.[name] ?? 1);

  const candidates = available.filter(
    (name) => AMBIENT_WEIGHTS[name] !== undefined && weightOf(name) > 0,
  );
  const totalWeight = candidates.reduce((sum, name) => sum + weightOf(name), 0);
  if (totalWeight <= 0) return null;

  let draw = rng() * totalWeight;
  for (const name of candidates) {
    draw -= weightOf(name);
    if (draw < 0) return name;
  }
  return candidates[candidates.length - 1]!;
}
